import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './RedistributionPanel.css';

const fmtHours = (v) => (v == null ? '–' : Number.isInteger(v) ? `${v}h` : `${v.toFixed(1)}h`);

const fmtDelta = (delta) => {
  if (delta === 0) return '0';
  const sign = delta > 0 ? '+' : '−';
  const abs = Math.abs(delta);
  return `${sign}${Number.isInteger(abs) ? abs : abs.toFixed(1)}h`;
};

const fmtPct = (base, value) => {
  if (base == null || base === 0) return '–';
  const pct = ((value - base) / base) * 100;
  if (pct === 0) return '0%';
  const sign = pct > 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(0)}%`;
};

const classifyPct = (base, value) => {
  if (base == null || base === 0 || value == null) return 'redistribution-delta--zero';
  const diff = value - base;
  if (Math.abs(diff) < 0.5) return 'redistribution-delta--zero';
  return diff > 0 ? 'redistribution-delta--add' : 'redistribution-delta--cut';
};

const EmployeeRow = ({ r, shapeSummary, finalHours }) => {
  const [showShapes, setShowShapes] = useState(false);
  const delta = (r.post_redistribution ?? 0) - (r.pre_redistribution ?? 0);
  const deltaClass = delta < 0 ? 'redistribution-delta--cut' : delta > 0 ? 'redistribution-delta--add' : 'redistribution-delta--zero';
  const pctLabel = fmtPct(r.pre_redistribution, r.post_redistribution);
  const finalVal = finalHours != null ? finalHours : null;
  const finalDevPctLabel = fmtPct(r.post_redistribution, finalVal);
  const finalDevClass = classifyPct(r.post_redistribution, finalVal);
  const realized = r.lp_realized_hours;
  const realizedDrift = realized != null ? realized - (r.pre_redistribution ?? 0) : null;
  const realizedClass = realizedDrift == null
    ? 'redistribution-delta--zero'
    : Math.abs(realizedDrift) < 0.5
      ? 'redistribution-delta--zero'
      : realizedDrift > 0
        ? 'redistribution-delta--add'
        : 'redistribution-delta--cut';

  const perShape = r.per_shape_allocation || {};
  const shapeEntries = Object.entries(perShape).sort((a, b) => b[1] - a[1]);
  const hasShapes = shapeEntries.length > 0;
  const shapeHint = hasShapes
    ? shapeEntries.map(([st, h]) => `${st.length > 14 ? st.slice(0, 12) + '…' : st}:${fmtHours(h)}`).join(', ')
    : '';

  return (
    <>
      <tr className={hasShapes ? 'redistribution-row--clickable' : ''} onClick={() => hasShapes && setShowShapes(p => !p)}>
        <td>
          {hasShapes && (
            <span className="redistribution-row__chev">{showShapes ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
          )}
          {r.name}
        </td>
        <td className="redistribution-role">{r.role_type}</td>
        <td className="num">{fmtHours(r.pre_redistribution)}</td>
        <td className="num">{fmtHours(r.post_redistribution)}</td>
        <td className={`num ${deltaClass}`}>{fmtDelta(delta)}</td>
        <td className={`num ${deltaClass}`}>{pctLabel}</td>
        <td
          className={`num ${finalDevClass}`}
          title={
            finalVal != null
              ? `Scheduled ${fmtHours(finalVal)} vs post-redistribution target ${fmtHours(r.post_redistribution)}`
              : 'No scheduled shifts'
          }
        >
          {finalVal != null ? fmtHours(finalVal) : '–'}
        </td>
        <td className={`num ${finalDevClass}`}>{finalDevPctLabel}</td>
        <td className={`num ${realizedClass}`} title={realized != null ? `LP assigned ${fmtHours(realized)} (target was ${fmtHours(r.pre_redistribution)})` : 'Not LP-solved'}>
          {realized != null ? fmtHours(realized) : '–'}
        </td>
        <td className="num">{r.hours_priority ?? '–'}</td>
        <td className="num">{r.min_weekly_hours > 0 ? fmtHours(r.min_weekly_hours) : '–'}</td>
        <td>
          {r.lp_solved && <span className="redistribution-pill redistribution-pill--lp">LP</span>}
          {!r.lp_solved && hasShapes && (
            <span className="redistribution-shapes-hint" title={shapeHint}>{shapeEntries.length} shape{shapeEntries.length === 1 ? '' : 's'}</span>
          )}
        </td>
      </tr>
      {showShapes && hasShapes && (
        <tr className="redistribution-shape-detail">
          <td colSpan={12}>
            <table className="redistribution-shape-table">
              <thead>
                <tr>
                  <th>Shape</th>
                  <th className="num">Allocated to {r.name.split(' ')[0]}</th>
                  <th className="num">Shape budget</th>
                  <th className="num">Shape demand</th>
                  <th className="num">LP allocated to shape</th>
                </tr>
              </thead>
              <tbody>
                {shapeEntries.map(([st, hours]) => {
                  const summary = (shapeSummary && shapeSummary[st]) || {};
                  return (
                    <tr key={st}>
                      <td>{st}</td>
                      <td className="num">{fmtHours(hours)}</td>
                      <td className="num">{summary.budget != null ? fmtHours(summary.budget) : '–'}</td>
                      <td className="num">{summary.demand != null ? fmtHours(summary.demand) : '–'}</td>
                      <td className="num">{summary.lp_allocated != null ? fmtHours(summary.lp_allocated) : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const RedistributionPanel = ({ redistribution, scheduledShifts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShapeSummary, setShowShapeSummary] = useState(false);

  const finalHoursByEmpId = useMemo(() => {
    const map = {};
    (Array.isArray(scheduledShifts) ? scheduledShifts : []).forEach((s) => {
      const empId = s && (s.employee_id || s.employeeId);
      if (!empId) return;
      const len = Number(s.length);
      if (!Number.isFinite(len)) return;
      map[empId] = (map[empId] || 0) + len;
    });
    return map;
  }, [scheduledShifts]);

  // Backwards-compatible: redistribution may be an array (old shape) or
  // { by_employee, shape_summary } (new shape).
  const { rows, shapeSummary } = useMemo(() => {
    if (Array.isArray(redistribution)) {
      return { rows: redistribution, shapeSummary: {} };
    }
    if (redistribution && typeof redistribution === 'object') {
      return {
        rows: Array.isArray(redistribution.by_employee) ? redistribution.by_employee : [],
        shapeSummary: redistribution.shape_summary || {},
      };
    }
    return { rows: [], shapeSummary: {} };
  }, [redistribution]);

  const summary = useMemo(() => {
    let cut = 0;
    let added = 0;
    let changedCount = 0;
    let lpCount = 0;
    rows.forEach((r) => {
      const delta = (r.post_redistribution ?? 0) - (r.pre_redistribution ?? 0);
      if (delta < 0) cut += -delta;
      else if (delta > 0) added += delta;
      if (delta !== 0) changedCount += 1;
      if (r.lp_solved) lpCount += 1;
    });
    return { cut, added, changedCount, lpCount, total: rows.length };
  }, [rows]);

  const shapeSummaryEntries = useMemo(
    () => Object.entries(shapeSummary).sort((a, b) => (b[1].demand || 0) - (a[1].demand || 0)),
    [shapeSummary],
  );

  if (rows.length === 0) {
    return null;
  }

  const summaryLine = [
    `${summary.changedCount}/${summary.total} changed`,
    summary.cut > 0 && `↓${fmtHours(summary.cut)} total`,
    summary.added > 0 && `↑${fmtHours(summary.added)} total`,
    summary.lpCount > 0 && `${summary.lpCount} LP-solved (excluded)`,
    shapeSummaryEntries.length > 0 && `${shapeSummaryEntries.length} shape${shapeSummaryEntries.length === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="redistribution-panel">
      <div
        className="redistribution-header"
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setIsExpanded((prev) => !prev);
        }}
      >
        <h3 className="redistribution-title">Hours redistribution</h3>
        <span className="redistribution-counts">{summaryLine}</span>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {isExpanded && (
        <>
          {shapeSummaryEntries.length > 0 && (
            <div className="redistribution-shape-summary">
              <div
                className="redistribution-shape-summary__header"
                onClick={() => setShowShapeSummary((p) => !p)}
                role="button"
                tabIndex={0}
              >
                <span>Per-shape demand &amp; budget</span>
                {showShapeSummary ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
              {showShapeSummary && (
                <table className="redistribution-shape-table">
                  <thead>
                    <tr>
                      <th>Shape</th>
                      <th className="num">Demand</th>
                      <th className="num">LP allocated</th>
                      <th className="num">Budget (residual)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shapeSummaryEntries.map(([st, s]) => (
                      <tr key={st}>
                        <td>{st}</td>
                        <td className="num">{fmtHours(s.demand)}</td>
                        <td className="num">{fmtHours(s.lp_allocated)}</td>
                        <td className={`num ${s.budget <= 0 ? 'redistribution-delta--zero' : ''}`}>
                          {fmtHours(s.budget)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <div className="redistribution-table-wrap">
            <table className="redistribution-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th className="num">Pre</th>
                  <th className="num">Post</th>
                  <th className="num">Δ</th>
                  <th className="num">Δ %</th>
                  <th className="num">Final</th>
                  <th className="num">Final vs Post</th>
                  <th className="num">LP realized</th>
                  <th className="num">Priority</th>
                  <th className="num">Min</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows
                  .slice()
                  .sort((a, b) => {
                    const da = (a.post_redistribution ?? 0) - (a.pre_redistribution ?? 0);
                    const db = (b.post_redistribution ?? 0) - (b.pre_redistribution ?? 0);
                    return da - db;
                  })
                  .map((r) => (
                    <EmployeeRow
                      key={r.id}
                      r={r}
                      shapeSummary={shapeSummary}
                      finalHours={finalHoursByEmpId[r.id]}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default RedistributionPanel;
